import { useRouterState } from '@tanstack/react-router'
import { useState } from 'react'

import { JellyIcon } from '@/components/icons'

import { AppLink as Link } from './AppLink'

/* The dev impersonator from spec §1 (role: Dev), prototyped as the design
   review tool: always floating bottom-right, switches which role's app you
   are looking at. Roles are URL segments (/<role>), so switching is just
   navigation — no state, no cookies. */
type Role = {
  id: string
  label: string
  person: string
  letter: string
  avatarClasses: string
} & ({ href: string; disabled?: false } | { href?: undefined; disabled: true })

const ROLES: Role[] = [
  {
    id: 'klient',
    label: 'Klient',
    person: 'Pizzerie U Pece',
    letter: 'P',
    avatarClasses: 'bg-brand text-cream',
    href: '/klient/prehled',
  },
  {
    id: 'fotograf',
    label: 'Fotograf',
    person: 'Tomáš Hájek',
    letter: 'T',
    avatarClasses: 'bg-sun text-ink',
    href: '/fotograf/prehled',
  },
  {
    id: 'spravce',
    label: 'Správce',
    person: 'Markeťačka Bára',
    letter: 'B',
    avatarClasses: 'bg-cream text-brand',
    href: '/spravce/prehled',
  },
  {
    id: 'manazer',
    label: 'Manažer',
    person: 'Adam Nykl',
    letter: 'A',
    avatarClasses: 'bg-surface text-ink/60',
    disabled: true,
  },
  {
    id: 'admin',
    label: 'Admin',
    person: 'Jednatelé',
    letter: 'J',
    avatarClasses: 'bg-surface text-ink/60',
    disabled: true,
  },
]

function RoleAvatar({ role, size = 'size-9' }: { role: Role; size?: string }) {
  return (
    <span
      aria-hidden
      className={`initial font-display flex shrink-0 items-center justify-center rounded-full ${size} ${role.avatarClasses}`}
    >
      {role.letter}
    </span>
  )
}

export function Impersonator() {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const [open, setOpen] = useState(false)

  const current =
    ROLES.find((role) => !role.disabled && pathname.startsWith(`/${role.id}`)) ?? ROLES[0]

  return (
    <div className="fixed right-4 bottom-24 z-50 lg:right-6 lg:bottom-6">
      {open && (
        <div className="absolute right-0 bottom-full mb-3 w-72 rounded-3xl bg-ink p-2.5 text-cream shadow-2xl">
          <p className="px-3 pt-2 pb-1.5 text-[10px] font-bold tracking-widest text-cream/40 uppercase">
            Dev · Impersonace
          </p>
          {ROLES.map((role) => {
            if (role.disabled) {
              return (
                <div
                  key={role.id}
                  className="flex items-center gap-3 rounded-2xl px-3 py-2.5 opacity-40"
                >
                  <RoleAvatar role={role} />
                  <div className="min-w-0">
                    <p className="text-sm font-bold">{role.label}</p>
                    <p className="truncate text-xs text-cream/60">{role.person}</p>
                  </div>
                  <span className="ml-auto shrink-0 rounded-full bg-cream/10 px-2 py-0.5 text-[9px] font-bold tracking-wider uppercase">
                    Později
                  </span>
                </div>
              )
            }
            const active = current.id === role.id
            return (
              <Link
                key={role.id}
                href={role.href}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-3 rounded-2xl px-3 py-2.5 transition-colors duration-150 hover:bg-cream/10 ${
                  active ? 'bg-cream/15' : ''
                }`}
              >
                <RoleAvatar role={role} />
                <div className="min-w-0">
                  <p className="text-sm font-bold">{role.label}</p>
                  <p className="truncate text-xs text-cream/60">{role.person}</p>
                </div>
                {active && (
                  <JellyIcon
                    name="check"
                    variant="fill"
                    className="ml-auto size-4 shrink-0 text-sun"
                  />
                )}
              </Link>
            )
          })}
        </div>
      )}

      <button
        type="button"
        aria-expanded={open}
        aria-label="Přepnout roli (dev impersonace)"
        onClick={() => setOpen((prev) => !prev)}
        className="flex cursor-pointer items-center gap-2.5 rounded-full bg-ink py-2 pr-4 pl-2 text-cream shadow-xl transition-transform duration-150 hover:scale-[1.03]"
      >
        <RoleAvatar role={current} size="size-8" />
        <span className="text-left">
          <span className="block text-[9px] leading-none font-bold tracking-widest text-cream/50 uppercase">
            Role
          </span>
          <span className="block text-sm leading-tight font-bold">{current.label}</span>
        </span>
        <JellyIcon
          name="angle-up"
          className={`size-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
    </div>
  )
}
